/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { SalesAgreement } from '../storage';
import MonthCard from './MonthCard';
import Timeline, { TimelineDot } from './Timeline';
import CollapseSection from '../components/CollapseSection';
import { isDrawdownRequestable } from '../../drawdown/v2/DrawdownRequestModal/utils';
import { DigitalFarmerAccount } from '../../azure-b2c/storage';
import { hasSchedule } from '../utils';
import dayjs from 'dayjs';

// Prop type for the component
interface SalesAgreementDeliveryScheduleProps {
  agreement: SalesAgreement;
  account: DigitalFarmerAccount | undefined;
  onRequestDelivery: (monthKey: Date) => void;
}

export interface DeliveryScheduleProduct {
  productDescription: string;
  quantity: {
    committed: number;
    released: number;
    remaining: number;
    delivered: number;
    invoiced: number;
  };
  overdrawn: boolean;
  previouslyOverdrawn: boolean;
  available: boolean;
}

export interface GroupedLine {
  monthKey: string;
  products: DeliveryScheduleProduct[];
  isOpen: boolean;
  status: 'Open' | 'Closed' | 'Future';
  isCurrentMonth: boolean;
  isNextMonth: boolean;
  isMonthRequestable: boolean;
  monthDate: Date;
  overdrawn: boolean;
}

const SalesAgreementDeliverySchedule: React.FC<SalesAgreementDeliveryScheduleProps> = ({
  agreement,
  account,
  onRequestDelivery,
}) => {
  const generateMonths = (startDate: Date, endDate: Date) => {
    const months = [];
    const date = new Date(startDate);
    date.setDate(1);

    while (date <= endDate) {
      months.push(new Date(date));
      date.setMonth(date.getMonth() + 1);
    }

    return months;
  };

  const months = generateMonths(new Date(agreement.startDate), new Date(agreement.endDate));

  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  const nextMonth = (currentMonth + 1) % 12;
  const nextMonthYear = nextMonth === 0 ? currentYear + 1 : currentYear;

  const [groupedLines, setGroupedLines] = useState<GroupedLine[]>([]);

  useEffect(() => {
    const groupedLines = months.map((monthDate) => {
      const monthKey = `${monthDate.toLocaleString('default', { month: 'long' })} ${monthDate.getFullYear()}`;

      // Filter lines for the current month
      const lines = agreement.rawLines.filter((line) => {
        const lineDate = new Date(line.scheduledDate);
        return (
          lineDate.getMonth() === monthDate.getMonth() &&
          lineDate.getFullYear() === monthDate.getFullYear()
        );
      });

      // Get all unique products from the agreement
      const uniqueProducts = Array.from(
        new Set(agreement.rawLines.map((line) => line.productDescription)),
      );

      // Create products array for the current month
      const products = uniqueProducts
        .map((product) => {
          const line = lines.find((line) => line.productDescription === product);

          const setProduct = agreement.lines.find((line) => line.productDescription === product);
          const prodStartDate = dayjs(setProduct?.startDate);
          const prodEndDate = dayjs(setProduct?.endDate);
          const currentMonth = dayjs(monthDate);

          if (currentMonth.isBefore(prodStartDate) || currentMonth.isAfter(prodEndDate)) {
            return null;
          }

          const committed = line ? line.quantity.committed : 0;
          const released = line ? line.quantity.released : 0;

          // Check if the product was overdrawn in previous months
          let previouslyOverdrawn = false;

          for (let i = 0; i < months.indexOf(monthDate); i++) {
            const previousMonth = months[i];

            const previousLines = agreement.rawLines.filter((line) => {
              const lineDate = new Date(line.scheduledDate);
              return (
                lineDate.getMonth() === previousMonth.getMonth() &&
                lineDate.getFullYear() === previousMonth.getFullYear()
              );
            });

            const previousLine = previousLines.find((line) => line.productDescription === product);
            if (previousLine) {
              if (previousLine.quantity.released > previousLine.quantity.committed) {
                previouslyOverdrawn = true;
                break; // No need to check further if already found overdrawn
              }
            }
          }

          return {
            productDescription: product,
            quantity: {
              committed,
              released,
              remaining: line ? line.quantity.remaining : 0,
              delivered: line ? line.quantity.delivered : 0,
              invoiced: line ? line.quantity.invoiced : 0,
            },
            overdrawn: released > committed, // Set overdrawn based on released vs committed
            previouslyOverdrawn, // Set previously overdrawn status
            available: !line, // Mark as available if no line is found
          };
        })
        .filter((a) => a !== null);

      // Calculate the total committed and released for previous months
      let totalCommitted = 0;
      let totalReleased = 0;
      let previouslyOverdrawnTotals = false;

      // Iterate through previous months to calculate totals
      for (let i = 0; i < months.indexOf(monthDate); i++) {
        const previousMonth = months[i];

        const previousLines = agreement.rawLines.filter((line) => {
          const lineDate = new Date(line.scheduledDate);
          return (
            lineDate.getMonth() === previousMonth.getMonth() &&
            lineDate.getFullYear() === previousMonth.getFullYear()
          );
        });

        const previousLine = previousLines.some((e) => e.quantity.released > e.quantity.committed);
        if (previousLine) {
          previouslyOverdrawnTotals = true;
        }
        previousLines.forEach((line) => {
          totalCommitted += line.quantity.committed;
          totalReleased += line.quantity.released;
        });
      }

      // Determine if the current month is overdrawn based on previous totals
      const isOverdrawn = totalReleased > totalCommitted || previouslyOverdrawnTotals;

      const isOpen = lines.length > 0;
      const isCurrentMonth =
        monthDate.getMonth() === currentMonth && monthDate.getFullYear() === currentYear;
      const isNextMonth =
        monthDate.getMonth() === nextMonth && monthDate.getFullYear() === nextMonthYear;

      let status: GroupedLine['status'] = 'Future';
      if (isCurrentMonth || isNextMonth) {
        status = 'Open';
      } else if (monthDate < currentDate) {
        status = 'Closed';
      }

      const isMonthRequestable = isDrawdownRequestable(account, agreement) && status === 'Open';

      return {
        monthKey,
        products,
        isOpen,
        status,
        isCurrentMonth,
        isNextMonth,
        monthDate,
        isMonthRequestable,
        overdrawn: isOverdrawn,
      };
    });

    setGroupedLines(groupedLines);
  }, [agreement, account]);

  const toggleCollapse = (monthKey: string) => {
    setGroupedLines((prev) =>
      prev.map((month) =>
        month.monthKey === monthKey ? { ...month, isOpen: !month.isOpen } : month,
      ),
    );
  };

  const hasNoSchedule = !hasSchedule(agreement.rawLines);

  if (hasNoSchedule) {
    return null;
  }

  return (
    <Box>
      <CollapseSection defaultOpen={true} title='Delivery schedule'>
        <Box display='flex'>
          <Timeline />
          <Box flex='1' width='100%'>
            {groupedLines.map(
              ({
                monthKey,
                isOpen,
                products,
                monthDate,
                overdrawn,
                status,
                isCurrentMonth,
                isMonthRequestable,
              }) => (
                <Box key={monthKey} mb={2} display='flex' position='relative'>
                  <TimelineDot />
                  <MonthCard
                    monthKey={monthKey}
                    products={products}
                    lineDate={monthDate}
                    status={status}
                    isCurrentMonth={isCurrentMonth}
                    isOpen={isOpen}
                    isOverdrawn={overdrawn}
                    isRequestable={isMonthRequestable}
                    toggleCollapse={() => toggleCollapse(monthKey)}
                    onRequestDelivery={onRequestDelivery}
                  />
                </Box>
              ),
            )}
          </Box>
        </Box>
      </CollapseSection>
    </Box>
  );
};

export default SalesAgreementDeliverySchedule;
